import React, { createContext, useContext, useEffect, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import api from "../api/axios";
import { runBackgroundRequest } from "../utils/backgroundRequestQueue";

export const HostFeedbackUnreadContext = createContext(null);

const clampInt = (value) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return 0;
  return Math.max(0, Math.floor(parsed));
};

const EMPTY_CONTEXT = {
  totalUnread: 0,
  unreadEventsCount: 0,
  byEventId: {},
  eventMetaById: {},
  refetch: async () => {},
};

export const HostFeedbackUnreadProvider = ({
  children,
  enabled = true,
  intervalMs = 60_000,
}) => {
  const { data, refetch } = useQuery({
    queryKey: ["host_feedback_unread_summary"],
    enabled: Boolean(enabled),
    queryFn: async () => {
      const response = await runBackgroundRequest(() => (
        api.get("/ratings/host-feedback/unread-summary/", {
          meta: { suppressGlobalError: true },
        })
      ));
      return response?.data || { total_unread: 0, unread_events_count: 0, events: [] };
    },
    refetchInterval: intervalMs,
    staleTime: 0,
    refetchOnWindowFocus: true,
  });

  useEffect(() => {
    if (!enabled || typeof window === "undefined") return undefined;
    const handleNotification = (event) => {
      const meta = event?.detail?.meta || {};
      const actionKind = String(meta.action_kind || meta.actionKind || "").toLowerCase();
      if (actionKind === "anonymous_host_feedback") {
        void refetch();
      }
    };
    window.addEventListener("vg:notification", handleNotification);
    return () => window.removeEventListener("vg:notification", handleNotification);
  }, [enabled, refetch]);

  const value = useMemo(() => {
    const totalUnread = clampInt(data?.total_unread);
    const unreadEventsCount = clampInt(data?.unread_events_count);
    const byEventId = {};
    const eventMetaById = {};
    const events = Array.isArray(data?.events) ? data.events : [];

    events.forEach((item) => {
      const eventId = item?.event_id != null ? String(item.event_id) : "";
      if (!eventId) return;
      const unreadCount = clampInt(item?.unread_count);
      byEventId[eventId] = unreadCount;
      eventMetaById[eventId] = {
        eventId: Number(item?.event_id || 0) || null,
        eventName: String(item?.event_name || ""),
        unreadCount,
      };
    });

    return {
      totalUnread,
      unreadEventsCount,
      byEventId,
      eventMetaById,
      refetch,
    };
  }, [data, refetch]);

  return (
    <HostFeedbackUnreadContext.Provider value={value}>
      {children}
    </HostFeedbackUnreadContext.Provider>
  );
};

export const useHostFeedbackUnread = () => {
  const context = useContext(HostFeedbackUnreadContext);
  return context || EMPTY_CONTEXT;
};
